/**
 * #1964 — the order STATUS groups appear in on the Notify page.
 *
 * ed, 2026-09-30 17:20: he sends by STATUS [PRIORITY], so every status list on
 * that page — **Send to: By Status** and the **Status filter under By Skill** —
 * must be in priority order, compared as DECIMALS. 17:22: *"employee seniority
 * has NOTHING to do with #1964. The only field is the STATUS priority,
 * employee_statuses.priority, which carries tenths."*
 *
 * ⚠ NOT `employees.seniority`. That is a different field, used all over
 * EmployeeController, and nothing here reads it.
 *
 * ⚠ DECIMALS, NOT INTEGERS. `employee_statuses.priority` is `char(36)` — a string
 * column — and ia8 carries ten decimal rows: `4026`, `4026.1`, `4026.2`, `4026.3`.
 * Truncating them collapses the group onto one number and hands the decision to
 * the name, which puts `D list 4026-1` last because `D 4` sorts before `D l`.
 * That shipped once and cs3tests caught it on ia8.
 *
 * ⚠ THE SERVER IS AUTHORITATIVE. StatusPriorityOrder (PHP) orders both
 * `employee/employee_status` and `employee_status_lite`. This mirror exists so
 * the two dropdowns provably apply ONE rule in the browser too, rather than one
 * trusting the server while the other re-sorts with a different comparator —
 * which is exactly how they came to disagree in the first place. Both are tested
 * against the same ia8 fixture so a drift between them shows up as a failure.
 */

/** `employee_statuses.priority` as a number. Empty, null and non-numeric read 0. */
export function priorityOf(row) {
  const v = row && row.priority;
  const n = parseFloat(v);

  return (v === '' || v === null || v === undefined || Number.isNaN(n)) ? 0 : n;
}

export function nameOf(row) {
  return String((row && row.name) || '');
}

/**
 * Priority ascending as a decimal, then natural order on the name.
 *
 * Numeric-aware and case-insensitive: the real data carries both `A List` and
 * `A list`, and names run `D 90`, `D 900`, `D 1000` where plain string order is
 * wrong.
 */
export function compareStatusPriority(a, b) {
  const pa = priorityOf(a);
  const pb = priorityOf(b);
  if (pa !== pb) return pa < pb ? -1 : 1;

  return nameOf(a).localeCompare(nameOf(b), undefined, { numeric: true, sensitivity: 'base' });
}

/** A new array in status-priority order. Does not mutate the input. */
export function sortByStatusPriority(list) {
  return (Array.isArray(list) ? list.slice() : []).sort(compareStatusPriority);
}

export default { priorityOf, nameOf, compareStatusPriority, sortByStatusPriority };
